// @school-apps/core/calendar — the pure occurrence expander (CAL·2). Hub + personal records in, a
// flat, sorted array of DATED occurrences within a window out. This is the shared engine the month
// grid (CAL·3) and personal events in the .ics (CAL·5) both read, so it lives here, once.
//
// PURE: no DB, no clock, no network, no tz/locale dependence. All date math is UTC (Date.UTC +
// getUTCDay, step by 86400000ms) — a date's weekday is computed the same everywhere. Central time
// only matters for "today", which is the VIEW's job, never this function's.
//
// TOLERANT like the ics emitter (ics.js): a malformed record is SKIPPED, never thrown — a meeting
// missing a valid day/time/term, an event failing the CAL·1 shape (neither/both of date and days,
// empty days, to<from, start without end), an assignment/exam without a valid date. Only a bad
// from/to bound throws (the window is required and must be well-formed).
import { DAY_WDAY, parseIsoDate } from "./ics.js";

const DAY_MS = 86_400_000;
const has = (v) => v !== undefined && v !== null;
const isTime = (t) => typeof t === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(t);
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

/**
 * Flatten meetings, personal events, assignments, and exams into dated occurrences within an
 * inclusive [from, to] window.
 * @param {object} input
 * @param {Array<object>} [input.meetings] `mtg_` payloads ({ id, day, start, end, course?, courseId?, termStart, termEnd })
 * @param {Array<object>} [input.events] `pev_` payloads (CAL·1 shape: one-off `date` XOR recurring `days`+`from`+`to`, optional time window, `color?`)
 * @param {Array<object>} [input.assignments] `asn_` payloads ({ id, courseId, title, due, done })
 * @param {Array<object>} [input.exams] `exm_` payloads ({ id, courseId, title, date })
 * @param {string} input.from inclusive "YYYY-MM-DD" lower bound (required)
 * @param {string} input.to inclusive "YYYY-MM-DD" upper bound (required)
 * @returns {Array<{ source: "meeting"|"event"|"assignment"|"exam", id: string, title: string, date: string, allDay: boolean, start?: string, end?: string, courseId?: string, color?: unknown, done?: boolean }>}
 *   sorted by (date, start ?? "", id)
 */
export function expandOccurrences({ meetings = [], events = [], assignments = [], exams = [], from, to } = {}) {
  const fromD = parseIsoDate(from);
  const toD = parseIsoDate(to);
  if (!fromD || !toD) {
    throw new Error(`expandOccurrences: from/to must be "YYYY-MM-DD" (got ${JSON.stringify(from)}..${JSON.stringify(to)})`);
  }

  // The window's dates, each with its UTC weekday — built once, reused by the recurring expansions.
  const windowDates = [];
  for (let t = fromD.getTime(); t <= toD.getTime(); t += DAY_MS) {
    const d = new Date(t);
    windowDates.push({ date: ymd(d), wday: d.getUTCDay() });
  }
  const inWindow = (date) => date >= from && date <= to; // lexical: both bounds are validated YYYY-MM-DD

  const out = [];

  // ── meetings: one timed occurrence per window date whose weekday == meeting.day, clipped to the term
  for (const m of meetings) {
    if (!m || !m.id || !(m.day in DAY_WDAY)) continue;
    if (!isTime(m.start) || !isTime(m.end)) continue;
    if (!parseIsoDate(m.termStart) || !parseIsoDate(m.termEnd)) continue;
    const wday = DAY_WDAY[m.day];
    const title = typeof m.course === "string" && m.course.trim() ? m.course.trim() : "Class";
    for (const w of windowDates) {
      if (w.wday !== wday) continue;
      if (w.date < m.termStart || w.date > m.termEnd) continue;
      const occ = { source: "meeting", id: m.id, title, date: w.date, allDay: false, start: m.start, end: m.end };
      if (has(m.courseId)) occ.courseId = m.courseId;
      out.push(occ);
    }
  }

  // ── personal events (CAL·1 shape). Skip anything that fails the one-off-XOR-recurring / time rules.
  for (const e of events) {
    if (!e || !e.id || typeof e.title !== "string" || !e.title.trim()) continue;
    const oneOff = has(e.date);
    const recurring = has(e.days) || has(e.from) || has(e.to);
    if (oneOff === recurring) continue; // neither or both -> malformed
    // optional time window: both-or-neither, valid HH:MM, end strictly after start
    const timed = has(e.start) || has(e.end);
    if (timed) {
      if (!has(e.start) || !has(e.end)) continue;
      if (!isTime(e.start) || !isTime(e.end) || e.end <= e.start) continue;
    }
    const title = e.title.trim();
    const emit = (date) => {
      const occ = { source: "event", id: e.id, title, date, allDay: !timed };
      if (timed) { occ.start = e.start; occ.end = e.end; }
      if (has(e.color)) occ.color = e.color;
      out.push(occ);
    };
    if (oneOff) {
      if (!parseIsoDate(e.date) || !inWindow(e.date)) continue;
      emit(e.date);
    } else {
      if (!Array.isArray(e.days) || e.days.length === 0 || !e.days.every((d) => d in DAY_WDAY)) continue;
      if (!parseIsoDate(e.from) || !parseIsoDate(e.to) || e.to < e.from) continue;
      const wdays = new Set(e.days.map((c) => DAY_WDAY[c]));
      for (const w of windowDates) {
        if (!wdays.has(w.wday)) continue;
        if (w.date < e.from || w.date > e.to) continue;
        emit(w.date);
      }
    }
  }

  // ── assignments: all-day on the due date; a done assignment is still shown (the calendar is the
  //    semester, not a todo list — mirrors ics.js). `done` is always carried.
  for (const a of assignments) {
    if (!a || !a.id || !parseIsoDate(a.due) || !inWindow(a.due)) continue;
    const occ = { source: "assignment", id: a.id, title: a.title, date: a.due, allDay: true, done: a.done === true };
    if (has(a.courseId)) occ.courseId = a.courseId;
    out.push(occ);
  }

  // ── exams: all-day on the exam date
  for (const x of exams) {
    if (!x || !x.id || !parseIsoDate(x.date) || !inWindow(x.date)) continue;
    const occ = { source: "exam", id: x.id, title: x.title, date: x.date, allDay: true };
    if (has(x.courseId)) occ.courseId = x.courseId;
    out.push(occ);
  }

  // canonical order: (date, start ?? "", id) — all-day (no start) sorts before timed on the same day
  out.sort((p, q) =>
    p.date < q.date ? -1 : p.date > q.date ? 1
      : (p.start ?? "") < (q.start ?? "") ? -1 : (p.start ?? "") > (q.start ?? "") ? 1
      : p.id < q.id ? -1 : p.id > q.id ? 1 : 0);
  return out;
}
